/**
 * Multi-org selector (A2F-244).
 *
 * Rendered in the app header only when the user has more than one active
 * org membership. On select, writes the selection to localStorage (via
 * ActiveOrgContext.setActiveOrgId) and reloads the page so all contexts
 * pick up the new org cleanly.
 *
 * The Lock icon is informational only (tooltip: "Requires Microsoft
 * sign-in"). SSO-required rows stay clickable — if the user picks one
 * while on a non-SSO session, the backend 403s and the
 * ORG_SWITCH_SSO_REQUIRED_EVENT flow surfaces the ssoBanner with a
 * "Sign in with Microsoft" action. The selector is a navigation aid,
 * not the enforcement point.
 *
 * A2F-990: rows where this user has no web-app access (webAppAccess ===
 * false — typically a ribbon/add-in-only `author` membership) ARE disabled,
 * because selecting one would 403 every request including /auth/me and strand
 * the user with no way back. `undefined` (older cached payloads) is treated
 * as "has access" so we never wrongly disable.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Lock, Check, Globe } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useActiveOrg } from '@/lib/ActiveOrgContext';
import { ORG_SWITCH_SSO_REQUIRED_EVENT } from '@/lib/api';
import { canSelectOrgInWebApp } from '@/lib/orgAccess';

export function OrgSelector() {
  const { user, login } = useAuth();
  const { activeOrg, orgs, setActiveOrgId } = useActiveOrg();
  const [open, setOpen] = useState(false);
  // A2F-990: banner carries an ssoRequired flag so the "Sign in with Microsoft"
  // CTA only shows for SSO rejections (the not-member / no-web-app-access arms
  // are resolved by the home-org fallback, not by re-authenticating).
  const [banner, setBanner] = useState<{ message: string; ssoRequired: boolean } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Surface an org-switch rejection from an X-Org-Id switch
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string; ssoRequired?: boolean }>).detail;
      setBanner({
        message:
          detail?.message
          ?? 'This organization requires SSO. Please sign in with Microsoft.',
        // Default true preserves prior behavior for any dispatcher that omits
        // the flag; api.ts always sets it explicitly.
        ssoRequired: detail?.ssoRequired ?? true,
      });
    };
    window.addEventListener(ORG_SWITCH_SSO_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(ORG_SWITCH_SSO_REQUIRED_EVENT, handler);
  }, []);

  if (!user || orgs.length <= 1) return null;

  const handlePick = (orgId: string) => {
    setOpen(false);
    setActiveOrgId(orgId);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-1 text-xs font-medium
                   text-slate-700 border border-slate-200 rounded-md
                   hover:bg-slate-50 focus:outline-none focus:ring-2
                   focus:ring-orange-200"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="max-w-[160px] truncate">
          {activeOrg?.name ?? 'Select organization'}
        </span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-72 bg-white border border-slate-200
                     rounded-md shadow-lg z-20 py-1"
          role="listbox"
        >
          {orgs.map(o => {
            const selected = activeOrg?.orgId === o.orgId;
            // A2F-990: disable orgs the user has no web-app access to (only
            // when explicitly false — undefined cached payloads stay
            // selectable). Selecting one would 403 every request incl.
            // /auth/me and strand the user.
            const noWebAccess = !canSelectOrgInWebApp(o);
            return (
              <button
                key={o.orgId}
                type="button"
                disabled={noWebAccess}
                onClick={() => { if (!noWebAccess) handlePick(o.orgId); }}
                className={
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left '
                  + (noWebAccess
                    ? 'text-slate-400 cursor-not-allowed'
                    : 'text-slate-700 hover:bg-slate-50')
                }
                role="option"
                aria-selected={selected}
                aria-disabled={noWebAccess}
                title={
                  noWebAccess
                    ? 'No web app access — available in the Word add-in only'
                    : undefined
                }
              >
                <span className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="truncate">{o.name}</span>
                  {o.isPublicSignup && (
                    <Globe
                      size={12}
                      className="text-slate-400 shrink-0"
                      aria-label="Public archive"
                    />
                  )}
                  {noWebAccess && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wide
                                     text-slate-400 border border-slate-200
                                     rounded px-1 py-px">
                      Add-in only
                    </span>
                  )}
                </span>
                <span className="text-xs text-slate-400">{o.role}</span>
                {o.requiresSso && (
                  <span
                    title="Requires Microsoft sign-in"
                    className="inline-flex"
                  >
                    <Lock size={14} className="text-slate-400" />
                  </span>
                )}
                {selected && (
                  <Check size={14} className="text-emerald-500" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {banner && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border
                        border-orange-200 rounded-md shadow-lg p-3 z-20">
          <p className="text-sm text-slate-700">{banner.message}</p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
            >
              Dismiss
            </button>
            {/* A2F-990: only SSO rejections are fixable by re-authenticating. */}
            {banner.ssoRequired && (
              <button
                type="button"
                onClick={() => { setBanner(null); login(); }}
                className="px-2 py-1 text-xs font-medium bg-orange-600 text-white
                           rounded hover:bg-orange-700"
              >
                Sign in with Microsoft
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
